import { ErrorMessage, Stack } from '@lightspeed/design-system-react'
import { useT } from '@transifex/react'
import React, { useEffect } from 'react'
import { trackCustomHeapEvent } from '@vend/utilities'
import { FulfilmentDetail } from '../../../../models/fulfilment'
import {
  HEAP_ADDRESS_NOT_VALIDATED_OBJECT,
  HEAP_ADDRESS_VALIDATED_OBJECT,
  HEAP_DELIVERY_SALE_ACTION,
  HEAP_NO_ADDRESS_OBJECT,
} from '../../sale-model.constant'
import { Addresses } from './address/Addresses'
import { AutotaxErrors } from './AutotaxErrors'
import { EmptyAddress } from './address/EmptyAddress'
import {
  AddressErrors,
  AddressErrorsStatus,
  useFulfillmentHelper,
} from './popover/hooks/useFulfillmentHelper'

interface FulfilmentAddressProps {
  fulfillmentDetail: FulfilmentDetail | undefined
  isModalOpeningOnError?: boolean
  lineItemLocalId?: string
  onSave?: (fulfillmentDetails: FulfilmentDetail[]) => void
}

export const LineItemFulfilmentAddress: React.FC<FulfilmentAddressProps> = ({
  fulfillmentDetail,
  isModalOpeningOnError,
  lineItemLocalId,
  onSave,
}: FulfilmentAddressProps) => {
  const t = useT()
  const { getLineItemFulfillmentAddressDetails } = useFulfillmentHelper()

  const {
    isSameBillingAddress,
    formattedShippingAddress,
    formattedBillingAddress,
    isAddressEmpty,
    error,
    status,
  } = getLineItemFulfillmentAddressDetails(fulfillmentDetail)

  const shouldShowError =
    !!error &&
    (!isModalOpeningOnError ||
      status !== AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING)

  const noShippingAddress = !fulfillmentDetail || isAddressEmpty

  const HasBillingAddressInModal =
    isModalOpeningOnError &&
    (status === AddressErrorsStatus.EMPTYSHIPPING_CORRECTBILLING ||
      status === AddressErrorsStatus.EMPTYSHIPPING_INCORRECTBILLING)

  const showEmptyAddressButton = noShippingAddress && !HasBillingAddressInModal

  useEffect(() => {
    const objectName = showEmptyAddressButton
      ? HEAP_NO_ADDRESS_OBJECT
      : status === AddressErrorsStatus.CORRECTSHIPPING_CORRECTBILLING
      ? HEAP_ADDRESS_VALIDATED_OBJECT
      : HEAP_ADDRESS_NOT_VALIDATED_OBJECT

    trackCustomHeapEvent({
      action: HEAP_DELIVERY_SALE_ACTION,
      object: objectName,
    })
    // intentionally omit dependencies to track this event only once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack space={4}>
      {showEmptyAddressButton ? (
        <EmptyAddress
          isModalOpeningOnError={isModalOpeningOnError}
          hasError={Boolean(error)}
          addressStatus={status}
          lineItemLocalId={lineItemLocalId}
          onSave={onSave}
        />
      ) : (
        <Addresses
          isSameBillingAddress={isSameBillingAddress}
          formattedShippingAddress={formattedShippingAddress}
          formattedBillingAddress={formattedBillingAddress}
          isModalOpeningOnError={isModalOpeningOnError}
          hasError={Boolean(error)}
          addressStatus={status}
          fulfilmentDetail={fulfillmentDetail}
          onSave={onSave}
          lineItemLocalId={lineItemLocalId}
        />
      )}
      {shouldShowError && (
        <ErrorMessage>
          {error === AddressErrors.ADD_ADDRESS &&
            t('Add shipping and billing address to complete the sale', {
              _context: 'Alert text',
            })}
          {error === AddressErrors.ADDRESS_INCORRECT &&
            t('Update incorrect address details to complete the sale', {
              _context: 'Alert text',
            })}
          {error === AddressErrors.ADDRESS_REQUIRED &&
            t('Shipping & billing address required for fulfillment', {
              _context: 'Alert text',
            })}
        </ErrorMessage>
      )}
      <AutotaxErrors
        countryId={fulfillmentDetail?.shippingAddress?.country_id}
      />
    </Stack>
  )
}
